<!--
  @component ReleasesPanel — the deploy workbench.
  tier:      screen content (inside the deploy IO chain)
  scope:     ship a release, watch it flow, see history. The live deploy
             action runs over the command channel; until that's wired
             the action is honestly "connect to deploy" (not a dead
             button) and the pipeline shows how a deploy will flow.
  reads:     useIdentityIO  → project · useIO → effecting
             useDeployRunEdge → run() + stage · useProvenanceIO → history
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeployRunEdge } from "../../../../global/io/edges/deployment/DeployRunEdge.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { useIO } from "../../../../global/io/IO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import StatusNote from "../../../../global/visuals/primitives/StatusNote.svelte";
  import StagePipeline, { type PipelineStage } from "../../../../global/visuals/primitives/StagePipeline.svelte";
  import { RELEASE_STAGES, STAGE_LABEL, STAGE_HINT, stageFromOp, type ReleaseStageId } from "./release-event-map.js";
  import BuildPanel from "../../../../global/visuals/BuildPanel.svelte";
  import type { SupplyRunSpec, DeploymentStreamEvent } from "../../../../global/io/nodes/deployment/deployment-io.js";

  const identity = useIdentityIO();
  const edge = useDeployRunEdge();
  const provenance = useProvenanceIO();
  const io = useIO();

  const project = $derived(identity.currentProject);
  const targetId = $derived(project?.id ?? "");
  const canDeploy = $derived(io.bundle.deployment.effecting);
  const connected = $derived(io.connected);
  let deploying = $state(false);
  let deployError = $state<string | null>(null);
  // When the current deploy began — scopes the live pipeline to THIS run so a
  // prior run's trailing "succeeded" event doesn't colour a fresh deploy.
  let deployStartedAt = $state(0);

  const stages: PipelineStage[] = RELEASE_STAGES.map((id) => ({ id, label: STAGE_LABEL[id] }));

  async function deploy(): Promise<void> {
    if (!targetId || !canDeploy) return;
    deploying = true;
    deployError = null;
    deployStartedAt = Date.now();
    try { await edge.run({ targetId } as unknown as SupplyRunSpec); }
    catch (e) { deployError = e instanceof Error ? e.message : String(e); }
    finally { deploying = false; }
  }

  // ── Live pipeline — drive the stage tracker from the edge stage + the
  //    release.* event stream (scoped to this run by deployStartedAt). ────────
  const EDGE_STAGE: Record<string, ReleaseStageId | ""> = {
    idle: "", previewing: "previewing", gated: "gated", running: "running",
    cutover: "cutover", succeeded: "succeeded", failed: "",
  };
  const runEvents = $derived(
    provenance.events.filter(
      (e: DeploymentStreamEvent) => e.op.startsWith("release.") && e.atMs >= deployStartedAt,
    ),
  );
  // The latest streamed event that names a stage wins; else fall back to the edge stage.
  const streamStage = $derived.by<ReleaseStageId | null>(() => {
    for (let i = runEvents.length - 1; i >= 0; i--) {
      const ev = runEvents[i];
      if (!ev) continue;
      const s = stageFromOp(ev.op);
      if (s) return s;
    }
    return null;
  });
  const pipelineCurrent = $derived(
    edge.stage === "failed" || edge.stage === "idle" ? "" : (streamStage ?? EDGE_STAGE[edge.stage] ?? ""),
  );
  const pipelineFailed = $derived(edge.stage === "failed" ? (streamStage ?? "running") : undefined);
  const pipelineCompleted = $derived(edge.stage === "succeeded" ? [...RELEASE_STAGES] : undefined);
  const activeStage = $derived(deploying ? (streamStage ?? (EDGE_STAGE[edge.stage] || null)) : null);

  // ── Success outcome — the live droplet URL (from the Fly appName the
  //    server reports during provisioning). ──────────────────────────────────
  const deployedAppName = $derived.by<string | null>(() => {
    for (let i = runEvents.length - 1; i >= 0; i--) {
      const ev = runEvents[i];
      if (!ev) continue;
      const d = ev.detail;
      if (d && typeof d === "object" && typeof (d as Record<string, unknown>).appName === "string") {
        return (d as Record<string, unknown>).appName as string;
      }
    }
    return null;
  });
  const deployedUrl = $derived(deployedAppName ? `https://${deployedAppName}.fly.dev` : null);
  const succeeded = $derived(edge.stage === "succeeded" && !deploying);

  // ── History ──────────────────────────────────────────────────────
  const OP_LABEL: Record<string, string> = {
    "release.previewing": "Preview started",
    "release.gated": "Gates checked",
    "release.running": "Provisioning",
    "release.cutover": "Cut over to new version",
    "release.drained": "Drained previous version",
    "release.succeeded": "Deploy succeeded",
  };
  function label(op: string): string {
    return OP_LABEL[op] ?? op.replace(/^[a-z]+\./, "").replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
  }
  function timeAgo(ms: number): string {
    const d = Date.now() - ms;
    if (d < 60_000) return "just now";
    if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
    if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
    return `${Math.floor(d / 86_400_000)}d ago`;
  }
  const history = $derived(
    provenance.events.filter((e: DeploymentStreamEvent) => e.op.startsWith("release.")).slice(-12).reverse(),
  );
</script>

{#if !project}
  <div class="notfound"><h1>Project not found</h1><p>This URL doesn't match a project you own.</p></div>
{:else}
  <div class="screen">
    <header class="head">
      <h1>Deployments</h1>
      <p class="sub">Ship a new version of your app, watch it roll out, and roll back if you need to.</p>
    </header>

    <!-- Deploy -->
    <Card elevated eyebrow="Ship" title="Deploy">
      {#snippet actions()}
        <button type="button" class="btn-primary" disabled={!canDeploy || !connected || deploying} onclick={() => void deploy()}>
          {deploying ? "Deploying…" : !connected ? "Connecting…" : "Deploy now"}
        </button>
      {/snippet}
      <div class="pipeline-wrap">
        <StagePipeline {stages} current={pipelineCurrent} failed={pipelineFailed} completed={pipelineCompleted} />
      </div>
      {#if activeStage}
        <p class="pipeline-help">{STAGE_HINT[activeStage]}</p>
      {:else}
        <p class="pipeline-help">Every deploy runs through these stages — preview and build, automated checks, a health-gated cutover, then a saved restore point.</p>
      {/if}
      {#if !canDeploy}
        <StatusNote tone="pending" message="Connect your deploy source to ship. Your image is set in Settings; the deploy connection lights this up." />
      {/if}
      {#if succeeded}
        <StatusNote
          tone="success"
          title="Deployed"
          message={deployedUrl ?? "Your release is live."}
          actionLabel={deployedUrl ? "Open" : undefined}
          onAction={deployedUrl ? () => window.open(deployedUrl, "_blank", "noopener") : undefined}
        />
      {/if}
      {#if deployError}
        <p class="deploy-err">{deployError}</p>
      {/if}
    </Card>

    <!-- Build from source -->
    <Card eyebrow="Ship" title="Build from source">
      {#if project}<BuildPanel projectId={targetId} />{/if}
    </Card>

    <!-- History -->
    <Card eyebrow="History" title="Recent deploys">
      {#if history.length === 0}
        <p class="empty">No deploys yet. When you ship, each release shows up here with its outcome.</p>
      {:else}
        <ul class="history">
          {#each history as e, i (`${e.atMs}-${e.op}-${i}`)}
            <li>
              <span class="h-dot" data-origin={e.origin}></span>
              <span class="h-label">{label(e.op)}</span>
              <span class="h-time">{timeAgo(e.atMs)}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </Card>

    <!-- Preview environments -->
    <Card eyebrow="Preview" title="Preview environments">
      <p class="empty">Spin up a temporary copy of your app for a branch or pull request.</p>
      <StatusNote tone="pending" message="Preview environments turn on with the deploy connection." />
    </Card>
  </div>
{/if}

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 1000px; font-family: var(--type-font); }
  .head h1 { margin: 0 0 var(--space-2); font-size: 24px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .sub { margin: 0; font-size: 13.5px; color: var(--text-secondary); }
  .empty { margin: 0 0 var(--space-3); font-size: 13px; color: var(--text-tertiary); line-height: 1.6; }

  .pipeline-wrap { padding: var(--space-2) 0 var(--space-4); overflow-x: auto; }
  .pipeline-help { margin: 0; font-size: 12.5px; color: var(--text-secondary); line-height: 1.6; }
  .deploy-err { margin: var(--space-3) 0 0; font-size: 12.5px; color: var(--error); line-height: 1.5; }

  .history { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
  .history li { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2) 0; border-top: 0.5px solid var(--border-subtle); }
  .history li:first-child { border-top: 0; }
  .h-dot { width: 7px; height: 7px; border-radius: var(--radius-pill); background: var(--text-tertiary); flex-shrink: 0; }
  .h-dot[data-origin="runtime"], .h-dot[data-origin="domain"] { background: var(--error); }
  .h-label { flex: 1; font-size: 13px; color: var(--text-secondary); }
  .h-time { font-size: 12px; color: var(--text-ghost); }

  .btn-primary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 36px; background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary:hover:not(:disabled) { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  .notfound { max-width: 460px; }
  .notfound h1 { margin: 0 0 var(--space-3); font-size: 20px; font-weight: 600; color: var(--text-primary); }
  .notfound p { margin: 0; font-size: 13.5px; color: var(--text-secondary); }
</style>
